import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import PlotComponent from 'react-plotly.js';
import env from './utils/env';

// Cast Plot component to fix React 18 type compatibility issue
const Plot = PlotComponent as unknown as React.ComponentType<{
    data: Array<Record<string, unknown>>;
    layout: Record<string, unknown>;
    config?: Record<string, unknown>;
    style?: React.CSSProperties;
}>;
import './App.css';
import styles from './SearchMetricsView.module.css';

interface MetricDataPoint {
    date: string;
    count: number;
}

interface SearchMetricsData {
    dates: string[];
    metrics: {
        searchTargets: MetricDataPoint[];
        searchAttempts: MetricDataPoint[];
        searchResults: MetricDataPoint[];
        reportSourcesCandidates: MetricDataPoint[];
        downloadedDocuments: MetricDataPoint[];
        foundPdfs: MetricDataPoint[];
    };
    last_updated: string;
}

const SearchMetricsView: React.FC = () => {
    const { token, isAuthenticated, authLoaded } = useAuth();
    const { t } = useTranslation();
    const [metricsData, setMetricsData] = useState<SearchMetricsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const apiUrl = env.apiUrl;

    const fetchMetrics = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${apiUrl}/admin/search-metrics`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setMetricsData(response.data);
        } catch (err: unknown) {
            console.error('Error fetching search metrics:', err);
            const errorMessage = axios.isAxiosError(err) && err.response?.data?.error
                ? err.response.data.error
                : t('searchMetrics.errorFetching');
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [token, apiUrl, t]);

    useEffect(() => {
        if (authLoaded && isAuthenticated && token) {
            fetchMetrics();
        }
    }, [authLoaded, isAuthenticated, token, fetchMetrics]);

    // Detect dark mode
    const isDarkMode = document.body.classList.contains('dark-mode');

    const plotLayout: Partial<Plotly.Layout> = {
        paper_bgcolor: isDarkMode ? '#2d3748' : '#ffffff',
        plot_bgcolor: isDarkMode ? '#2d3748' : '#ffffff',
        font: { color: isDarkMode ? '#e2e8f0' : '#333333' },
        xaxis: {
            gridcolor: isDarkMode ? '#4a5568' : '#e0e0e0',
            tickangle: -45
        },
        yaxis: {
            gridcolor: isDarkMode ? '#4a5568' : '#e0e0e0'
        },
        margin: { t: 30, b: 80, l: 60, r: 40 },
        legend: { orientation: 'h' as const, y: -0.25 },
        autosize: true
    };

    const createTrace = (data: MetricDataPoint[], name: string, color: string): Plotly.Data => ({
        x: data.map(d => d.date),
        y: data.map(d => d.count),
        type: 'scatter' as const,
        mode: 'lines+markers' as const,
        name,
        line: { color, width: 2 },
        marker: { size: 6 }
    });

    if (!authLoaded) {
        return (
            <div className="container">
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>{t('searchMetrics.loadingAuth')}</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="container">
                <div className={styles.errorMessage}>
                    <i className="fas fa-exclamation-triangle"></i>
                    {t('searchMetrics.authRequired')}
                </div>
            </div>
        );
    }

    return (
        <div className={`container ${styles.dashboard}`}>
            <div className="page-header">
                <h1>
                    <i className="fas fa-chart-area"></i>
                    {t('searchMetrics.title')}
                </h1>
                <p className="page-subtitle">
                    {t('searchMetrics.subtitle')}
                </p>
                {metricsData?.last_updated && (
                    <div className="last-updated">
                        <i className="fas fa-clock"></i>
                        {t('searchMetrics.lastUpdated')}: {new Date(metricsData.last_updated).toLocaleString()}
                    </div>
                )}
                <button
                    onClick={fetchMetrics}
                    disabled={loading}
                    className="refresh-button"
                >
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                    {t('searchMetrics.refreshData')}
                </button>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <i className="fas fa-exclamation-triangle"></i>
                    {error}
                </div>
            )}

            {loading && !metricsData && (
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p>{t('searchMetrics.loadingMetrics')}</p>
                </div>
            )}

            {!error && metricsData && (
                <div className={styles.grid}>
                    {/* Search Targets Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-bullseye"></i> {t('searchMetrics.searchTargetsTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.searchTargets, t('searchMetrics.searchTargets'), '#4CAF50')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Search Attempts Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-search"></i> {t('searchMetrics.searchAttemptsTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.searchAttempts, t('searchMetrics.searchAttempts'), '#2196F3')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Search Results Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-list"></i> {t('searchMetrics.searchResultsTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.searchResults, t('searchMetrics.searchResults'), '#FF9800')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Report Sources Candidates Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-file-alt"></i> {t('searchMetrics.reportSourcesCandidatesTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.reportSourcesCandidates, t('searchMetrics.reportSourcesCandidates'), '#9C27B0')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Downloaded Documents Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-download"></i> {t('searchMetrics.downloadedDocumentsTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.downloadedDocuments, t('searchMetrics.downloadedDocuments'), '#f44336')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Found PDFs Chart */}
                    <div className={styles.card}>
                        <h3><i className="fas fa-file-pdf"></i> {t('searchMetrics.foundPdfsTitle')}</h3>
                        <Plot
                            data={[createTrace(metricsData.metrics.foundPdfs, t('searchMetrics.foundPdfs'), '#607D8B')]}
                            layout={{ ...plotLayout }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '280px' }}
                        />
                    </div>

                    {/* Combined Overview Chart */}
                    <div className={`${styles.card} ${styles.cardFullWidth}`}>
                        <h3><i className="fas fa-chart-line"></i> {t('searchMetrics.combinedOverviewTitle')}</h3>
                        <Plot
                            data={[
                                createTrace(metricsData.metrics.searchTargets, t('searchMetrics.searchTargets'), '#4CAF50'),
                                createTrace(metricsData.metrics.searchAttempts, t('searchMetrics.searchAttempts'), '#2196F3'),
                                createTrace(metricsData.metrics.searchResults, t('searchMetrics.searchResults'), '#FF9800'),
                                createTrace(metricsData.metrics.reportSourcesCandidates, t('searchMetrics.reportSourcesCandidates'), '#9C27B0'),
                                createTrace(metricsData.metrics.downloadedDocuments, t('searchMetrics.downloadedDocuments'), '#f44336'),
                                createTrace(metricsData.metrics.foundPdfs, t('searchMetrics.foundPdfs'), '#607D8B')
                            ]}
                            layout={{ ...plotLayout, margin: { ...plotLayout.margin, b: 100 } }}
                            config={{ responsive: true, displayModeBar: false }}
                            style={{ width: '100%', height: '400px' }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchMetricsView;
